import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Typography, Grid, Paper, Stack, IconButton, Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircleOutlined, RefreshOutlined, ErrorOutlined,

} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { systemApi } from '../../../api';
import SetupGuide from '../../../components/onboarding/SetupGuide';
import { activeLocale } from '../../../utils/datetime';

interface SecurityItem { label: string; value: string; ok: boolean; }

interface OverviewData {
  total_workers: number;
  active_workers: number;
  total_roles: number;
  total_worker_groups: number;
  pending_invites: number;
  recent_audit_count: number;
}

const SystemOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [securityItems, setSecurityItems] = useState<SecurityItem[]>([
    { label: t('system:overview.security.passwordPolicyLabel'), value: t('system:overview.security.loading'), ok: true },
    { label: t('system:security.twoFactorCardTitle'), value: t('system:overview.security.loading'), ok: true },
  ]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await systemApi.systemOverviewApi.get() as OverviewData;
      setData(res);
    } catch { /* fallback to null */ }
    finally { setLoading(false); }

    // Fetch security settings for status card
    try {
      const sec = await systemApi.systemSettingsApi.getSecurity() as { data: Record<string, any> };
      const d = sec?.data ?? {};
      setSecurityItems([
        {
          label: t('system:overview.security.passwordPolicyLabel'),
          value: t('system:security.statusEnforced'),
          ok: true,
        },
        {
          label: t('system:security.twoFactorCardTitle'),
          value: d.require_2fa_for_admins
            ? t('system:security.statusRequired')
            : t('system:security.statusOptional'),
          ok: !!d.require_2fa_for_admins,
        },
      ]);
    } catch { /* keep defaults */ }
  }, [t]);

  useEffect(() => { fetch(); }, [fetch]);

  const kpis = data ? [
    { label: t('system:overview.kpis.totalWorkers'), value: data.total_workers, color: 'primary.main', path: '/organization/workers/active' },
    { label: t('system:overview.kpis.activeWorkers'), value: data.active_workers, color: 'success.main', path: '/organization/workers/active' },
    { label: t('system:overview.kpis.pendingInvites'), value: data.pending_invites, color: data.pending_invites > 0 ? 'warning.main' : 'success.main', path: '/organization/workers/active' },
    { label: t('system:overview.kpis.roles'), value: data.total_roles, color: 'info.main', path: '/system/access-control/roles' },
  ] : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('system:overview.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('system:overview.subtitle')}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}><IconButton size="small" onClick={fetch} sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#fff', '&:hover': { bgcolor: '#f5f5f5' } }}><RefreshOutlined fontSize="small" /></IconButton></Tooltip>
        </Stack>
      </Box>

      {/* Setup Guide — backend-driven (ADR-019 §D8) */}
      <SetupGuide section="system" />

      {/* KPIs — live from /v1/system/overview */}
      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
      ) : (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {kpis.map(k => (
            <Grid item xs={6} md={3} key={k.label}>
              <Paper elevation={0} onClick={() => navigate(k.path)} sx={{
                // p: 2.5, not 1.5 — the KPI titles sat at x=148 while the Billing card and the
                // shared Setup Guide sat at 157. The shared component sets the axis; a page-level
                // sibling has to meet it (svdy-ui §15: one alignment axis per page).
                p: 2.5, borderRadius: 2, cursor: 'pointer', width: '100%',
                border: '1px solid', borderColor: 'rgba(0,0,0,0.12)',
                transition: 'border-color 120ms',
                '&:hover': { borderColor: k.color },
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', display: 'block', mb: 0.5 }}>{k.label}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: k.color, lineHeight: 1 }}>{k.value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Billing + Support summaries (2-column) */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <BillingSummaryCard navigate={navigate} />
        </Grid>
        <Grid item xs={12} md={6}>
          <SupportSummaryCard navigate={navigate} />
        </Grid>
      </Grid>

      {/* Security — compact inline */}
      <Paper elevation={0} sx={{ width: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{t('system:overview.security.cardTitle')}</Typography>
          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }} onClick={() => navigate('/system/settings/security')}>{t('system:overview.security.configureLink')}</Typography>
        </Stack>
        <Stack direction="row" spacing={3}>
          {securityItems.map(s => (
            <Stack key={s.label} direction="row" alignItems="center" gap={0.75}>
              {s.ok ? <CheckCircleOutlined sx={{ fontSize: 14, color: 'success.main' }} /> : <ErrorOutlined sx={{ fontSize: 14, color: 'warning.main' }} />}
              <Typography variant="caption" sx={{ fontSize: '0.72rem' }}><strong>{s.label}:</strong> {s.value}</Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      {/* `icon={false}`: measured on the owner's screenshot, the Alert's icon sat at x=154 —
          on the card axis — while its MESSAGE was pushed to x=187 by MUI's icon slot (icon 22px
          + 12px gap), against every other text origin on the page (148/153/157). Dropping the
          slot puts the text on the axis. The informational cue survives without it:
          `variant="outlined"` + `severity="info"` already tints the border blue.

          This is the treatment `Attendance/Geofences/GeofenceRulesHint.tsx` already uses for the
          same thing — a page-level footer hint — so it is the established shape here, not a new
          one. */}
      <Alert severity="info" variant="outlined" icon={false} sx={{ width: '100%', mt: 2, fontSize: '0.75rem' }}>
        <span dangerouslySetInnerHTML={{ __html: t('system:overview.auditLogsHint') }} />
      </Alert>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Billing summary card
// ---------------------------------------------------------------------------

const BillingSummaryCard: React.FC<{ navigate: (p: string) => void }> = ({ navigate }) => {
  const { t } = useTranslation();
  const [sub, setSub] = React.useState<any>(null);

  React.useEffect(() => {
    import('../../../api').then(({ subscriptionApi }) => {
      subscriptionApi.currentApi.get().then(setSub).catch(() => {});
    });
  }, []);

  const planName = sub?.plan?.name ?? sub?.plan_id ?? '—';
  const status = sub?.status ?? '—';
  const periodEnd = sub?.current_period_end ? new Date(sub.current_period_end).toLocaleDateString(activeLocale()) : '—';

  return (
    <Paper elevation={0} onClick={() => navigate('/system/billing')} sx={{
      p: 2.5, borderRadius: 2, cursor: 'pointer', height: '100%',
      border: '1px solid', borderColor: 'rgba(0,0,0,0.12)',
      transition: 'border-color 120ms',
      '&:hover': { borderColor: 'primary.main' },
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{t('system:overview.billing.cardTitle')}</Typography>
        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>{t('system:overview.billing.manageLink')}</Typography>
      </Stack>
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">{t('system:overview.billing.plan')}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{planName}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">{t('system:overview.billing.status')}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: status === 'active' ? 'success.main' : status === 'past_due' ? 'error.main' : 'text.primary', textTransform: 'capitalize' }}>{status}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">{t('system:overview.billing.nextPayment')}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{periodEnd}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Support summary card
// ---------------------------------------------------------------------------

const SupportSummaryCard: React.FC<{ navigate: (p: string) => void }> = ({ navigate }) => {
  const { t } = useTranslation();
  const [stats, setStats] = React.useState<{ total: number; open: number; resolved: number }>({ total: 0, open: 0, resolved: 0 });

  React.useEffect(() => {
    import('../../../api').then(({ supportApi }) => {
      supportApi.list({ page_size: 100 }).then((res: any) => {
        const items = res?.items ?? [];
        setStats({
          total: items.length,
          open: items.filter((c: any) => c.status === 'open' || c.status === 'in_progress').length,
          resolved: items.filter((c: any) => c.status === 'resolved' || c.status === 'closed').length,
        });
      }).catch(() => {});
    });
  }, []);

  return (
    <Paper elevation={0} onClick={() => navigate('/system/support')} sx={{
      p: 2.5, borderRadius: 2, cursor: 'pointer', height: '100%',
      border: '1px solid', borderColor: 'rgba(0,0,0,0.12)',
      transition: 'border-color 120ms',
      '&:hover': { borderColor: 'primary.main' },
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{t('system:overview.support.cardTitle')}</Typography>
        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>{t('system:overview.support.viewAllLink')}</Typography>
      </Stack>
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">{t('system:overview.support.totalCases')}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{stats.total}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">{t('system:overview.support.openInProgress')}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: stats.open > 0 ? 'warning.main' : 'success.main' }}>{stats.open}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">{t('system:overview.support.resolvedClosed')}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main' }}>{stats.resolved}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

export default SystemOverview;
